import { useEffect, useRef, useState } from 'react'
import { getCustomer, createCustomer, updateCustomer } from '../api'
import { useTabs, useDataVersionUnlessDirty, useDirtyGuard, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useEditLock } from '../layout/useEditLock'
import RichTextEditor from '../components/RichTextEditor'
import InfoTip from '../components/InfoTip'
import CountryPicker from '../components/CountryPicker'
import { checkBeforeSave } from '../forms/checkForm'
import { fill, CUSTOMER_STALE, STALE_RELOAD } from '../../shared/texts'
import { PAGES_CUSTOMER_FORM_TEXTS as T } from '../../shared/texts'
import { DOCUMENT_URL_TIP, unknownPlaceholderWarning } from '../lookups'

const empty = {
    displayName: '', company: '', email: '', vatId: '', isBusiness: false,
    notes: '', invoiceMailSubject: '', invoiceMailBody: '',
    address: { line1: '', postalCode: '', city: '', country: '' },
}

// States the TAX CONSEQUENCE, and nothing else. The first attempt named the customer type
// ("Privatkunde") and got it wrong on the very case this whole change is about: a company
// without a VAT id was labelled a private customer — a company field filled in, `isBusiness`
// not yet set, and the line asserting something plainly false. That is the same mistake as the
// old switch, only phrased differently.
//
// So it says only what we actually know: whether the invoice carries VAT. `isBusiness` and the
// company name merely add the reassurance that yes, we noticed this is a business — and that it
// changes nothing without a VAT id.
function customerTaxNote(isBusiness: boolean, vatId: string, company: string): string {
    if (vatId.trim()) {
        return T.taxReverseCharge
    }
    return isBusiness || company.trim()
        ? T.taxBusinessNoVatId
        : T.taxNoVatId
}

export default function CustomerForm({ tabId, id }: { tabId?: string, id?: string }) {
    const formRef = useRef<HTMLFormElement>(null)
    const { publishChange, closeTab } = useTabs()
    const { toast } = useToast()
    const { can } = useAuth()
    const canWrite = can('customers', 'write')
    const lock = useEditLock(id ? `customer:${id}` : null)
    const [form, setForm] = useState<typeof empty>(empty)
    const [baseline, setBaseline] = useState<typeof empty>(empty) // last saved/loaded snapshot
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const grantedToasted = useRef(false)
    const dirty = JSON.stringify(form) !== JSON.stringify(baseline)
    // Reload when another client saves it or a sale changes it (customer.changed carries the id) —
    // not while there is unsaved input; that is caught on save instead (409, below).
    const recordVersion = useDataVersionUnlessDirty(id ? `customers:${id}` : '', dirty)
    // updatedAt as loaded — sent with the save, refused by the server if it moved.
    const [loadedAt, setLoadedAt] = useState<string | undefined>()
    const [stale, setStale] = useState(false)
    const [reloadKey, setReloadKey] = useState(0)

    // Notify (once) when the edit lock is handed over after waiting.
    useEffect(() => {
        if (lock.justGranted && !grantedToasted.current) {
            grantedToasted.current = true
            toast(T.canEditNow)
        }
    }, [lock.justGranted, toast])

    useEffect(() => {
        if (!id) return
        getCustomer(id).then((c) => {
            const loaded = {
                displayName: c.displayName, company: c.company ?? '', email: c.email,
                vatId: c.vatId ?? '', isBusiness: c.isBusiness, notes: c.notes ?? '',
                invoiceMailSubject: c.invoiceMailSubject ?? '', invoiceMailBody: c.invoiceMailBody ?? '',
                address: {
                    line1: c.address?.line1 ?? '', postalCode: c.address?.postalCode ?? '',
                    city: c.address?.city ?? '', country: c.address?.country ?? '',
                },
            }
            setForm(loaded); setBaseline(loaded)
            setLoadedAt(c.updatedAt); setStale(false); setError('')
        }).catch((e) => setError(e?.message ?? T.loadFailed))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, recordVersion, reloadKey])

    const set = (patch: Partial<typeof empty>) => setForm((f) => ({ ...f, ...patch }))
    const setAddr = (patch: Partial<typeof empty.address>) => setForm((f) => ({ ...f, address: { ...f.address, ...patch } }))
    const readOnly = !canWrite // role lacks customers:write → view only
    const locked = !lock.canEdit
    const disabled = locked || busy || readOnly

    // Persist; resolve true on success (used by both the form submit and the
    // close-confirm dialog). Marks the form clean so the tab's ● clears.
    async function save(): Promise<SaveResult> {
        // Required fields and formats from the markup — one check for every form (forms/checkForm.ts).
        const invalid = checkBeforeSave(formRef.current, setError)
        if (invalid) return invalid
        setBusy(true); setError(''); setStale(false)
        try {
            if (id) {
                const saved = await updateCustomer(id, { ...form, expectedUpdatedAt: loadedAt })
                setLoadedAt(saved.updatedAt) // a second save before any reload must not look stale
                publishChange('customers', id); setBaseline(form); toast(T.saved)
            }
            else { await createCustomer(form); publishChange('customers'); setBaseline(form); toast(T.created); closeTab('customer-new') }
            const warning = unknownPlaceholderWarning(form.invoiceMailSubject, form.invoiceMailBody)
            if (warning) toast(warning, 'warn')
            return { ok: true }
        } catch (e: any) {
            // Changed elsewhere since loading: nothing was saved, the input stays as it is.
            const isStale = e?.response?.status === 409 && e?.response?.data?.code === 'stale'
            if (isStale) setStale(true)
            const msg = isStale ? CUSTOMER_STALE : e?.response?.data?.error ?? e?.message ?? T.saveFailed
            setError(msg)
            return { ok: false, error: msg }
        } finally { setBusy(false) }
    }

    function submit(e: React.FormEvent) { e.preventDefault(); void save() }
    useDirtyGuard(tabId, dirty && !locked && !readOnly, save)

    // autoComplete="off" sits on the form AND on the individual fields: this records ANOTHER
    // person's data, so every suggestion the browser makes is wrong by definition — it would
    // offer the logged-in employee's address, not the customer's. Browsers honour the
    // form-level attribute unevenly, hence both.
    return (
        <form ref={formRef} noValidate onSubmit={submit} className="form-page" autoComplete="off">
            <div className="form-top">
            <div className="form-header">
                <h1>{readOnly ? T.titleView : id ? T.titleEdit : T.titleNew}</h1>
                {!readOnly && <button type="submit" disabled={disabled || !dirty}>{busy ? '…' : id ? T.apply : T.create}</button>}
            </div>

            {readOnly && <div className="notice">{T.readOnly}</div>}

            {locked && (
                <div className="notice warn">
                    {T.lockedQueue}
                    {lock.position > 0 ? fill(T.lockedPosition, { position: lock.position }) : ''}{T.lockedAfter}
                </div>
            )}
            {error && (
                <div className="notice err">
                    {error}
                    {stale && <> <button type="button" className="link-btn" onClick={() => setReloadKey((k) => k + 1)}>{STALE_RELOAD}</button></>}
                </div>
            )}
            </div>

            <fieldset disabled={disabled} className="form-body" style={{ border: 0, padding: 0, margin: 0 }}>
                <div className="panel">
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.name}</label>
                            <input type="text" name="customer-name" autoComplete="off" value={form.displayName} required onChange={(e) => set({ displayName: e.target.value })} /></div>
                        <div className="field"><label>{T.company}</label>
                            <input type="text" name="customer-company" autoComplete="off" value={form.company} onChange={(e) => set({ company: e.target.value })} /></div>
                    </div>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.email}</label>
                            <input type="email" name="customer-email" autoComplete="off" value={form.email} required onChange={(e) => set({ email: e.target.value })} /></div>
                    </div>
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field"><label>{T.street}</label>
                            <input type="text" name="customer-street" autoComplete="off" value={form.address.line1} onChange={(e) => setAddr({ line1: e.target.value })} /></div>
                        <div className="field" style={{ maxWidth: 120 }}><label>{T.postalCode}</label>
                            <input type="text" name="customer-zip" autoComplete="off" value={form.address.postalCode} onChange={(e) => setAddr({ postalCode: e.target.value })} /></div>
                        <div className="field"><label>{T.city}</label>
                            <input type="text" name="customer-city" autoComplete="off" value={form.address.city} onChange={(e) => setAddr({ city: e.target.value })} /></div>
                        <div className="field" style={{ maxWidth: 260 }}><label>{T.country}</label>
                            <CountryPicker name="customer-country" value={form.address.country} onChange={(country) => setAddr({ country })} /></div>
                    </div>
                    {/* The VAT id sits HERE, where one looks for it — next to the tax status —
                        and no longer above the address block, where it was found only by
                        searching. It is the field that actually decides the taxation.

                        The B2B switch that used to stand here is gone. It looked like a tax
                        control and was none: `isBusiness` is read nowhere for a decision, it
                        never reaches the invoice, and resolveReverseCharge asks the VAT id and
                        the country instead. Switching it on without a VAT id still produced a
                        taxed invoice — while it suggested the opposite. What the shop sends is
                        still accepted and stored (SaleTaxContext is unchanged); it is only no
                        longer editable here, and shown for what it is. */}
                    <div className="row" style={{ alignItems: 'flex-start' }}>
                        <div className="field" style={{ maxWidth: 260 }}>
                            <label>{T.vatId} <InfoTip>{T.vatIdTip}</InfoTip></label>
                            <input type="text" name="customer-vat-id" autoComplete="off" placeholder={T.vatIdPlaceholder}
                                value={form.vatId} onChange={(e) => set({ vatId: e.target.value })} />
                        </div>
                        <div className="field" style={{ alignSelf: 'flex-end' }}>
                            <span className="hint">{customerTaxNote(form.isBusiness, form.vatId, form.company)}</span>
                        </div>
                    </div>
                    <div className="field">
                        <label>{T.mailSubject} <InfoTip>{T.mailTipBefore}{'{month}'}, {'{year}'}.</InfoTip></label>
                        <input type="text" name="customer-invoice-mail-subject" autoComplete="off"
                            value={form.invoiceMailSubject} onChange={(e) => set({ invoiceMailSubject: e.target.value })} />
                    </div>
                    <div className="field">
                        <label>{T.mailBody} <InfoTip>{T.mailTipBefore}{'{name}'}, {'{month}'}, {'{year}'}, {T.mailTipProductList}. {DOCUMENT_URL_TIP}</InfoTip></label>
                        <RichTextEditor value={form.invoiceMailBody} onChange={(html) => set({ invoiceMailBody: html })} />
                    </div>
                </div>
            </fieldset>
        </form>
    )
}
